/**
 * GENERATED FILE — DO NOT EDIT.
 * Run `pnpm a2a-contract:generate` after changing the
 * canonical Pydantic contract.
 */
import type {
  AuthorSopArtifact,
  AuthorSopInput,
  AuthorSopOutput,
  AuthorSopPause,
  AuthorSopResume,
  AuthorSopSignal,
  BrowserUseArtifact,
  BrowserUseInput,
  BrowserUseOutput,
  BrowserUsePause,
  BrowserUseResume,
  BrowserUseSignal,
  ContractMetadata,
  MockUiStudioArtifact,
  MockUiStudioInput,
  MockUiStudioOutput,
  MockUiStudioPause,
  MockUiStudioResume,
  MockUiStudioSignal,
  OperatorStep,
  RunError,
  RunSopArtifact,
  RunSopInput,
  RunSopOutput,
  RunSopSignal,
  SopBranch,
  SopParam,
  SopStep,
  TaskRef,
} from "./types.js";

/** Compile-time association between every skill and its wire DTOs. */
export interface SkillDefinitions {
  "mock_ui.studio": {
    input: MockUiStudioInput;
    resume: MockUiStudioResume;
    signal: MockUiStudioSignal;
    pause: MockUiStudioPause;
    output: MockUiStudioOutput;
    artifact: MockUiStudioArtifact;
  };
  "operator.author_sop": {
    input: AuthorSopInput;
    resume: AuthorSopResume;
    signal: AuthorSopSignal;
    pause: AuthorSopPause;
    output: AuthorSopOutput;
    artifact: AuthorSopArtifact;
  };
  "operator.browser_use": {
    input: BrowserUseInput;
    resume: BrowserUseResume;
    signal: BrowserUseSignal;
    pause: BrowserUsePause;
    output: BrowserUseOutput;
    artifact: BrowserUseArtifact;
  };
  "operator.run_sop": {
    input: RunSopInput;
    resume: never;
    signal: RunSopSignal;
    pause: never;
    output: RunSopOutput;
    artifact: RunSopArtifact;
  };
}

/** Every public JSON Schema root and its exact TypeScript type. */
export interface ContractSchemaDefinitions {
  AuthorSopArtifact: AuthorSopArtifact;
  AuthorSopInput: AuthorSopInput;
  AuthorSopOutput: AuthorSopOutput;
  AuthorSopPause: AuthorSopPause;
  AuthorSopResume: AuthorSopResume;
  AuthorSopSignal: AuthorSopSignal;
  BrowserUseArtifact: BrowserUseArtifact;
  BrowserUseInput: BrowserUseInput;
  BrowserUseOutput: BrowserUseOutput;
  BrowserUsePause: BrowserUsePause;
  BrowserUseResume: BrowserUseResume;
  BrowserUseSignal: BrowserUseSignal;
  ContractMetadata: ContractMetadata;
  MockUiStudioArtifact: MockUiStudioArtifact;
  MockUiStudioInput: MockUiStudioInput;
  MockUiStudioOutput: MockUiStudioOutput;
  MockUiStudioPause: MockUiStudioPause;
  MockUiStudioResume: MockUiStudioResume;
  MockUiStudioSignal: MockUiStudioSignal;
  OperatorStep: OperatorStep;
  RunError: RunError;
  RunSopArtifact: RunSopArtifact;
  RunSopInput: RunSopInput;
  RunSopOutput: RunSopOutput;
  RunSopSignal: RunSopSignal;
  SopBranch: SopBranch;
  SopParam: SopParam;
  SopStep: SopStep;
  TaskRef: TaskRef;
}

export type ContractSchemaName = keyof ContractSchemaDefinitions;

/** Named routing constants generated from the canonical skill registry. */
export const A2A_SKILL_IDS = {
  authorSop: "operator.author_sop",
  browserUse: "operator.browser_use",
  mockUiStudio: "mock_ui.studio",
  runSop: "operator.run_sop",
} as const;

export const skillIds = [
  "mock_ui.studio",
  "operator.author_sop",
  "operator.browser_use",
  "operator.run_sop",
] as const;

/** Signal discriminators derived from each generated skill schema. */
export const skillSignalKinds = {
  "mock_ui.studio": ["heartbeat", "step"],
  "operator.author_sop": [
    "heartbeat",
    "sop_params",
    "sop_phase",
    "sop_retract",
    "sop_step",
    "step",
  ],
  "operator.browser_use": [
    "heartbeat",
    "sop_replay_branch",
    "sop_replay_step",
    "sop_replay_step_started",
    "step",
  ],
  "operator.run_sop": [
    "heartbeat",
    "sop_replay_branch",
    "sop_replay_step",
    "sop_replay_step_started",
    "step",
  ],
} as const;

/** Exhaustive generated signal discriminator vocabulary. */
export const signalKinds = [
  "heartbeat",
  "sop_params",
  "sop_phase",
  "sop_replay_branch",
  "sop_replay_step",
  "sop_replay_step_started",
  "sop_retract",
  "sop_step",
  "step",
] as const;

/** Named common schema roots generated from the canonical registry. */
export const commonSchemaNames = {
  contractMetadata: "ContractMetadata",
  operatorStep: "OperatorStep",
  runError: "RunError",
  sopBranch: "SopBranch",
  sopParam: "SopParam",
  sopStep: "SopStep",
  taskRef: "TaskRef",
} as const;

/** Agent Card and documentation metadata, excluding schema root names. */
export const skillMetadata = {
  "mock_ui.studio": {
    description:
      "Builds and iterates on a static mock UI in an isolated sandbox, with typed preview pauses, edited files, a state graph, and evaluation paths.",
    inputModes: ["text/plain", "application/json", "image/png"],
    name: "Mock UI Studio",
    outputModes: ["text/plain", "application/json", "image/png"],
    resumable: true,
    tags: ["mock-ui", "sandbox", "coding", "human-in-the-loop"],
  },
  "operator.author_sop": {
    description:
      "Co-authors a structured, replayable SOP with a human and supports annotations, takeover, free-roam capture, phases, and alternate paths.",
    inputModes: ["text/plain", "application/json", "image/png"],
    name: "SOP Author",
    outputModes: ["text/plain", "application/json", "image/png"],
    resumable: true,
    tags: ["browser", "sop", "authoring", "human-in-the-loop"],
  },
  "operator.browser_use": {
    description:
      "Drives a caller-provided browser to complete a natural-language task, streaming redacted reasoning, actions, screenshots, and typed results.",
    inputModes: ["text/plain", "application/json"],
    name: "Browser Operator",
    outputModes: ["text/plain", "application/json", "image/png"],
    resumable: true,
    tags: ["browser", "automation", "computer-use"],
  },
  "operator.run_sop": {
    description:
      "Replays a flattened SOP deterministically, self-heals drift, records branch decisions, and returns persistable repairs and complete tallies.",
    inputModes: ["text/plain", "application/json"],
    name: "SOP Replay",
    outputModes: ["text/plain", "application/json", "image/png"],
    resumable: false,
    tags: ["browser", "sop", "replay", "self-healing"],
  },
} as const;

export const skillSchemaNames = {
  "mock_ui.studio": {
    artifact: "MockUiStudioArtifact",
    input: "MockUiStudioInput",
    output: "MockUiStudioOutput",
    pause: "MockUiStudioPause",
    resume: "MockUiStudioResume",
    signal: "MockUiStudioSignal",
  },
  "operator.author_sop": {
    artifact: "AuthorSopArtifact",
    input: "AuthorSopInput",
    output: "AuthorSopOutput",
    pause: "AuthorSopPause",
    resume: "AuthorSopResume",
    signal: "AuthorSopSignal",
  },
  "operator.browser_use": {
    artifact: "BrowserUseArtifact",
    input: "BrowserUseInput",
    output: "BrowserUseOutput",
    pause: "BrowserUsePause",
    resume: "BrowserUseResume",
    signal: "BrowserUseSignal",
  },
  "operator.run_sop": {
    artifact: "RunSopArtifact",
    input: "RunSopInput",
    output: "RunSopOutput",
    pause: null,
    resume: null,
    signal: "RunSopSignal",
  },
} as const;
